import type { Difficulty } from './types';

export interface OpponentStory {
  image: string;
  defeatImage: string;
  chapter: string;
  location: string;
  epithet: string;
  backstory: string;
  encounter: string;
  hiddenPast: string;
  lesson: string;
  winQuote: string;
  lossQuote: string;
  drawQuote: string;
  inscription: string;
}

export const WORLD_STORY = {
  title: '雲門十二局',
  subtitle: '一只無名棋匣，十二位守局人，一條通往雲門的棋路。',
  intro: '傳說雲城不收門票，只收十二盤棋。山路曾在一場兵亂後封閉，十二位留下來的人，各自守住一段無法忘記的往事。旅人持一只無名棋匣上山，每過一局，匣底便顯出一段舊刻；每一段刻痕，也像替守局人解開一個多年未解的結。走到雲門，才會知道他們等的不是最強棋手，而是懂得何時進、何時守、何時放下的人。',
  image: '/story/cloud-citadel-twelve.webp',
  completedInscription: '觀勢知取，聯勢敢捨；明攻能守，積微惜子；制形通變，終局問心。',
} as const;

export const OPPONENT_STORIES: Record<Difficulty, OpponentStory> = {
  beginner: {
    image: '/story/beginner-aya.webp', defeatImage: '/story/defeated-beginner.webp', chapter: '第一局', location: '竹院初逢', epithet: '拾子少年',
    backstory: '阿芽在山腳棋院長大，日日替師兄們收棋，卻從沒被當成真正的棋手。他把每一盤旁觀過的棋，都悄悄記在一冊皺巴巴的譜裡。',
    encounter: '你進竹院時，他正獨自把散落的棋子排回原位。看見你手上的無名棋匣，他鼓起勇氣，第一次主動向旅人邀局。',
    hiddenPast: '阿芽的父親曾是替雲城送棋譜的腳夫，三年前上山後便沒有回來。那冊舊譜的最後一頁，只留下半幅棋局與一句「看清下一步」。他想知道父親究竟輸在棋盤上，還是選擇了另一條路。',
    lesson: '先看清，再落子。阿芽偶爾會失誤，卻會認真回應每一次威脅。',
    winQuote: '原來輸一局，也能知道下一步要往哪裡走。請替我看看雲門。',
    lossQuote: '我真的贏了？那本棋譜，今天可以添上我的名字了。',
    drawQuote: '你也在學，我也在學。這盤棋就留著，下次再寫完吧。',
    inscription: '觀勢',
  },
  trader: {
    image: '/story/trader-iron-abacus.webp', defeatImage: '/story/defeated-trader.webp', chapter: '第二局', location: '市橋換子', epithet: '局中估客',
    backstory: '鐵算盤曾因一場豪賭失去家業，從此相信棋盤上沒有白拿的便宜。每一顆子、每一條線，他都要算清代價。',
    encounter: '市橋人聲鼎沸，他卻在攤前留了一張空棋桌。想過橋的人不必付錢，只要證明自己懂得交換。',
    hiddenPast: '沒有人知道，那場豪賭其實是他替一群欠債的街坊作保。他保住了別人的屋瓦，卻讓妻女遠走。多年來他把每一筆輸贏都記得分明，唯獨不敢計算那次選擇究竟值不值得。',
    lesson: '衡量交換，精簡盤面。他偏愛兌子，也可能為眼前利益錯估長遠局勢。',
    winQuote: '這一筆，是我算漏了。棋盤的價值，果然不只寫在帳面上。',
    lossQuote: '承讓。你付出的每一顆子，我都記在帳上了。',
    drawQuote: '你不欠我，我也不欠你。這盤帳，平了。',
    inscription: '知取',
  },
  cannonhorse: {
    image: '/story/cannonhorse-thunder-steed.webp', defeatImage: '/story/defeated-cannonhorse.webp', chapter: '第三局', location: '驛台雙響', epithet: '驛路先鋒',
    backstory: '雷駒是雲城最快的驛使。戰火中，他靠一騎一炮送出救城軍報，也從此把馬的轉折與炮的長線練成同一口氣。',
    encounter: '驛台上兩聲號響，一聲催馬，一聲試炮。他將最後一封信壓在棋盤下，說贏了才准你帶上山。',
    hiddenPast: '那封信並不是軍報，而是一名守城士兵寫給家人的告別。雷駒晚到了一個時辰，從此再也不允許自己慢下來。如今收信的人早已不在，他仍想把信送到雲門，彷彿只要抵達，遲來的承諾就還算數。',
    lesson: '馬炮呼應，製造雙重威脅。他的組合拳凌厲，但節奏被打斷時也會露出空隙。',
    winQuote: '你的棋比驛馬更快。這封信，交給你送到山頂。',
    lossQuote: '馬到，炮響。等你追上這個節奏，我們再跑一趟。',
    drawQuote: '同時抵達。好，這封信我們各護一程。',
    inscription: '聯勢',
  },
  gambiteer: {
    image: '/story/gambiteer-red-blade.webp', defeatImage: '/story/defeated-gambiteer.webp', chapter: '第四局', location: '赤灘焚舟', epithet: '斷歸路者',
    backstory: '赤刃原是水軍斥候。一次撤軍時，他親手燒掉最後一艘渡船，讓追兵以為全軍已無退路；士卒因此死戰突圍，也有人永遠沒能回到對岸。',
    encounter: '赤灘終年留著焦木的氣味。他把一匹紅馬放在棋盤最前方，說真正的先手不是快，而是願意為下一步付出什麼。',
    hiddenPast: '史書把那一戰寫成焚舟奇勝，只有赤刃記得船上原本還替傷兵留了位置。他不怕輸子，卻害怕自己早已把犧牲當成習慣。每一盤棋，他都在尋找一種不必留下任何人的勝法。',
    lesson: '以材料換時間，以先手換將區壓力。他的棄子都必須帶著攻勢；若你能熄掉第一把火，缺少的子力就會反過來追討代價。',
    winQuote: '你接住了我扔出去的那顆子，卻沒有被火帶走。原來留退路，也能向前。',
    lossQuote: '船已經燒了。當你還在計算回頭路時，我的棋早已渡江。',
    drawQuote: '火燒到河心就停了。這一次，我們都替對方留了一條船。',
    inscription: '敢捨',
  },
  attacker: {
    image: '/story/attacker-gale.webp', defeatImage: '/story/defeated-attacker.webp', chapter: '第五局', location: '風垣攻城', epithet: '破陣將軍',
    backstory: '烈風曾是邊城先鋒，習慣在敵人站穩之前撕開缺口。退下戰場後，他把未竟的衝鋒全留在棋盤上。',
    encounter: '風垣殘旗獵獵，他把棋盤架在舊城門前。想入城，就得在他的攻勢裡找到一口喘息。',
    hiddenPast: '他最後一次出征時，為追逐潰敵越過軍令所定的界線，贏了戰場，卻來不及回援故城。眾人稱他破陣將軍，他聽見的卻總是身後那扇關不上的門。這一局，他想知道進攻是否也能成為一種守護。',
    lesson: '搶先手、壓王城，以攻代守。他會讓壓力一波接一波，也可能因追攻過深留下後方。',
    winQuote: '你沒有被風推著走，反而找到了風眼。這道城門，開了。',
    lossQuote: '守得住第一陣，不代表守得住下一陣。再來時，別把先手讓給我。',
    drawQuote: '攻不破，守不倒。今天這陣風，就停在城門外。',
    inscription: '明攻',
  },
  defender: {
    image: '/story/defender-dark-wall.webp', defeatImage: '/story/defeated-defender.webp', chapter: '第六局', location: '雨關守城', epithet: '不動城壁',
    backstory: '玄壁守過一座無援的雨關。那一夜，他不求殲敵，只求城門後的百姓平安走完最後一里路。',
    encounter: '山雨封道，他默默替你移開路障，卻把一盤棋擺在關門內：能拆開他的防線，才有資格走更險的路。',
    hiddenPast: '雨關最後仍然失守，但撤離的百姓全數活了下來。朝廷的戰報只寫「守將棄關」，沒有人替他補上後半句。玄壁從不辯解，只把當夜每一道防線重現在棋盤上，等一個人看懂他為何選擇後退。',
    lesson: '補強弱點、誘敵深入、等待反擊。他不急著贏，但會讓冒進付出代價。',
    winQuote: '你不是撞開城牆，而是找到門。真正的攻手，懂得耐心。',
    lossQuote: '雨會停，城牆還在。回去看看，你從哪一步開始著急。',
    drawQuote: '你進不了關，我也留不住你。雨停之前，就算彼此守住了。',
    inscription: '能守',
  },
  pawn: {
    image: '/story/pawn-green-grain.webp', defeatImage: '/story/defeated-pawn.webp', chapter: '第七局', location: '青田渡河', epithet: '五卒田守',
    backstory: '青穗不是將軍，而是替雲城守田的農女。兵亂那年，軍糧斷絕，她帶著五戶人家沿河逐畦復耕，硬是把一袋種子變成全城冬糧。',
    encounter: '渡口沒有城牆，只有五支插在田埂上的青旗。她先替你盛一碗粥，才把五枚兵整齊排上棋盤：小子走得慢，但每一步都算數。',
    hiddenPast: '青穗的弟弟曾跟著徵糧隊離開，臨行前笑她只懂田地、不懂天下。多年後，隊伍的名冊送回來，弟弟那一欄沒有生死，只寫著「失散」。她守住每一粒糧，也是在替一個可能回來的人留飯。',
    lesson: '保存中兵、推兵過河、用小子封住馬路。她不靠一記重拳，而是讓五條細小的路慢慢連成無法忽視的壓力。',
    winQuote: '你沒有踩壞田，也沒有讓兵白走。這一季的路，就交給你繼續種。',
    lossQuote: '別小看一粒種子。等你發現它過了河，整片田都已經站起來了。',
    drawQuote: '河水各分一半，田也各留一半。明年再看誰的兵先長高。',
    inscription: '積微',
  },
  miser: {
    image: '/story/miser-vault.webp', defeatImage: '/story/defeated-miser.webp', chapter: '第八局', location: '藏樓惜子', epithet: '百子藏主',
    backstory: '金庫看守的不是金銀，而是戰亂後流散的古棋。每顆殘子背後都有一位棋手的名字，因此他從不輕易棄子。',
    encounter: '藏樓裡，千百枚棋子靜得像在聽。金庫請你選一顆最捨不得失去的子，然後用整盤棋證明理由。',
    hiddenPast: '藏樓最深處有一枚燒焦的紅兵，是他幼年從故鄉廢墟裡拾回的唯一物件。他收藏天下殘子，不只是惜物，而是害怕任何名字再次被遺忘。然而藏得越多，他越分不清保存與囚禁之間的界線。',
    lesson: '護子、避險、累積小優勢。他極少白送材料，但過度惜子也可能錯過進攻時機。',
    winQuote: '你讓棋子活著，不是因為捨不得，而是知道何時讓它完成使命。',
    lossQuote: '每一顆留下的子，最後都會替你說話。你聽見了嗎？',
    drawQuote: '棋子都走了很遠，也都回得了家。這樣很好。',
    inscription: '惜子',
  },
  blockader: {
    image: '/story/blockader-river-lock.webp', defeatImage: '/story/defeated-blockader.webp', chapter: '第九局', location: '鎖江無渡', epithet: '閉路舟師',
    backstory: '無渡曾是河道司的舟師，熟悉每一道暗流與淺灘。兵亂時，他沒有擊沉敵船，而是沉下自己的鐵鏈與舊舟，把整條江變成誰也無法通行的迷陣。',
    encounter: '江面看似平靜，所有渡船卻都繫在岸邊。他把棋盤擺在封橋中央，請你不靠硬撞，找出一條仍能讓車馬通過的路。',
    hiddenPast: '封江救了上游三城，也斷了下游百姓的退路。無渡後來逐一打撈沉船，卻始終少找到一艘。他相信那艘船還在霧裡，所以每逢夜潮便點燈等候，不肯宣布河道真正重開。',
    lesson: '壓縮活動力、封車路、卡馬腿、借炮線制形。他不急著取子，而是讓你的好棋一格一格消失。',
    winQuote: '你沒有斬斷鐵鏈，只讓水自己找到出口。這條江，終於可以再開了。',
    lossQuote: '路不是被牆堵住的，是你每退一步，親手把出口留在了身後。',
    drawQuote: '潮來潮去，鏈仍在，船也還在。今晚誰都不必冒險過江。',
    inscription: '制形',
  },
  scholar: {
    image: '/story/scholar-ink-manual.webp', defeatImage: '/story/defeated-scholar.webp', chapter: '第十局', location: '墨閣問譜', epithet: '百譜先生',
    backstory: '百譜先生抄過雲城所有傳世棋譜，能從第一步猜出一盤棋的年代與師承。人們說他無局不識，他卻從不在任何譜末署名。',
    encounter: '墨閣四壁都是殘譜，每一張都只寫到布局結束。他隨手抽出一卷，請你走進熟路，再在譜盡之處走出自己的第一步。',
    hiddenPast: '先生年少時曾代替成名棋手完成一部巨著，換來母親的醫藥，卻也答應永不承認自己的筆跡。多年後，那位名家已逝，世人仍把他的創見背得滾瓜爛熟。守著百譜，他真正想找的是一局不必借用別人名字的棋。',
    lesson: '熟悉多種紅黑布局與轉型次序，會在相同首著後走向不同陣式。背譜只能帶他到門口，脫譜後的判斷才是勝負開始。',
    winQuote: '你走出了譜上沒有的那一步，而且有自己的理由。這一頁，該署你的名字。',
    lossQuote: '你記得招法，卻忘了它為何存在。譜不是答案，只是前人留下的問題。',
    drawQuote: '我們走到同一頁，也同時把筆停下。下一次，各寫各的後半局。',
    inscription: '通變',
  },
  veteran: {
    image: '/story/veteran-autumn-lantern.webp', defeatImage: '/story/defeated-veteran.webp', chapter: '第十一局', location: '殘亭收官', epithet: '秋燈老人',
    backstory: '秋燈替無數棋手收存未下完的棋。他相信人的性情藏在殘局裡：子越少，越沒有地方遮掩。',
    encounter: '殘亭只亮一盞燈，桌上是一盤等了多年的終局。他不問你從哪裡來，只問你是否願意把每一步走到底。',
    hiddenPast: '桌上那盤殘局屬於他的弟子。少年當年只差一步便能和棋，卻因急著赴約而笑說「明日再下」，此後再也沒有回亭。秋燈守著未落下的那一子，並非等答案，而是在學習接受有些棋局永遠不會結束。',
    lesson: '計算兌換、王的活動與兵的遠路。開局或許平淡，越到殘局越難撼動。',
    winQuote: '燈沒有變亮，是你終於看清了。帶著這份安靜，上雲門吧。',
    lossQuote: '棋少了，心裡的雜音反而多了。等你能安靜走完，再來找我。',
    drawQuote: '有些殘局不是走不完，而是值得留給下一盞燈。',
    inscription: '終局',
  },
  master: {
    image: '/story/master-cloud-hermit.webp', defeatImage: '/story/defeated-master.webp', chapter: '第十二局・終局', location: '雲門問心', epithet: '一級守關人',
    backstory: '雲隱曾走遍各地求一敗，最後卻把姓名留在雲外。他守的不是冠冕，而是一道問題：棋手能否超越自己最熟悉的那一手。',
    encounter: '十二段舊刻在棋匣底合成一句話。雲門打開，他已坐在棋盤另一端，像是早知道你會來。',
    hiddenPast: '無名棋匣原是雲隱年少時所製。他曾用它收集所有勝局，直到發現自己記得每一次勝利，卻說不出任何一位敗者後來去了哪裡。於是他將棋匣送下山，請七位舊友各留一段刻痕，也把最後一段問題留給未來的持匣人。',
    lesson: '均衡、深算、因勢而變。他沒有固定破綻，會測試你是否真正理解前七局。',
    winQuote: '你帶上山的不是答案，而是選擇。從今天起，雲門也為你留一個座位。',
    lossQuote: '你已經走到門前。別急著證明自己，先問那一步為何非走不可。',
    drawQuote: '雲沒有勝負。你已走到這裡，下一次就讓棋替你回答。',
    inscription: '問心',
  },
};

export function resultStoryQuote(difficulty: Difficulty, result: 'win' | 'loss' | 'draw'): string {
  const story = OPPONENT_STORIES[difficulty];
  return result === 'win' ? story.winQuote : result === 'loss' ? story.lossQuote : story.drawQuote;
}
